import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

import { treatmentPlanActions } from '../../../store';
import { TREATMENT_PLAN_V3_VERSION } from '../../../constants';

import type { AppDispatch, RootState } from '../../../store';
import type { ITreatmentPlan } from '../../../utils/types';
import type { ResolvedTreatmentPlanVersion, UseTreatmentPlanVersionResult } from './types';

export default function useTreatmentPlanVersion(): UseTreatmentPlanVersionResult {
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch<AppDispatch>();
  const { current, list } = useSelector((state: RootState) => state.treatmentPlans);
  const [resolvedVersion, setResolvedVersion] = useState<ResolvedTreatmentPlanVersion | null>(null);
  const [requestedTreatmentPlanId, setRequestedTreatmentPlanId] = useState<string | null>(null);
  const [failedTreatmentPlanId, setFailedTreatmentPlanId] = useState<string | null>(null);
  const latestRequestedTreatmentPlanIdRef = useRef<string | null>(null);

  const parsedTreatmentPlanId = Number(treatmentPlanId);
  const hasTreatmentPlanId = Number.isInteger(parsedTreatmentPlanId);
  const loadedTreatmentPlan =
    current?.id === parsedTreatmentPlanId
      ? current
      : list.find((plan) => plan.id === parsedTreatmentPlanId);
  const isVersionLatched = resolvedVersion?.treatmentPlanId === treatmentPlanId;

  let version: number | undefined;
  if (loadedTreatmentPlan) {
    version = loadedTreatmentPlan.version;
  } else if (isVersionLatched) {
    version = resolvedVersion?.version;
  }

  // The status bar clears the loaded plan when it unmounts, so the version is latched per plan.
  useEffect(() => {
    if (!treatmentPlanId || !loadedTreatmentPlan || isVersionLatched) return;
    setResolvedVersion({ treatmentPlanId, version: loadedTreatmentPlan.version });
  }, [isVersionLatched, loadedTreatmentPlan, treatmentPlanId]);

  useEffect(() => {
    if (!treatmentPlanId || !hasTreatmentPlanId || loadedTreatmentPlan || isVersionLatched) return;
    if (requestedTreatmentPlanId === treatmentPlanId) return;
    setRequestedTreatmentPlanId(treatmentPlanId);
    latestRequestedTreatmentPlanIdRef.current = treatmentPlanId;
    // Shared plan state is written by other consumers too, so this request resolves its own version.
    dispatch(treatmentPlanActions.getTreatmentPlanById({ treatmentPlanId, getAccessTokenSilently }))
      .unwrap()
      .then(
        (plan: ITreatmentPlan) => {
          if (latestRequestedTreatmentPlanIdRef.current !== treatmentPlanId) return;
          setResolvedVersion({ treatmentPlanId, version: plan.version });
        },
        () => {
          if (latestRequestedTreatmentPlanIdRef.current !== treatmentPlanId) return;
          setFailedTreatmentPlanId(treatmentPlanId);
        },
      );
  }, [
    dispatch,
    getAccessTokenSilently,
    hasTreatmentPlanId,
    isVersionLatched,
    loadedTreatmentPlan,
    requestedTreatmentPlanId,
    treatmentPlanId,
  ]);

  const retryVersionResolution = useCallback(() => {
    setFailedTreatmentPlanId(null);
    setRequestedTreatmentPlanId(null);
  }, []);

  return {
    isTreatmentPlanV3: version === TREATMENT_PLAN_V3_VERSION,
    isResolvingVersion:
      hasTreatmentPlanId &&
      !loadedTreatmentPlan &&
      !isVersionLatched &&
      failedTreatmentPlanId !== treatmentPlanId,
    hasVersionError:
      !loadedTreatmentPlan && !isVersionLatched && failedTreatmentPlanId === treatmentPlanId,
    retryVersionResolution,
  };
}
